import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import resize from 'FileManagerLib/utils/resize';
import { FLAT_EARTH, OLAF, GREAT_WHITE } from 'HubStyleTokens/colors';
import { WEB_FONT_MEDIUM_WEIGHT } from 'HubStyleTokens/misc';
import unescapedText from 'I18n/utils/unescapedText';
import getLang from 'I18n/utils/getLang';
import { rgba } from 'UIComponents/core/Color';
import UIClickable from 'UIComponents/button/UIClickable';
import UIImage from 'UIComponents/image/UIImage';
import UIBox from 'UIComponents/layout/UIBox';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIScrollContainer from 'UIComponents/scroll/UIScrollContainer';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import { requestIdleCallback } from 'marketplace-ui-common/utils/requestIdleCallback';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import ScreenshotsModal from 'marketplace-ui-common/components/screenshotsModal/ScreenshotsModal';
import { getAssetEventSelectorId } from '../../utils/hsAnalytics';
import { getPublicDetailPageFullUrl } from '../../utils/urls';
import VideoPlayer from './VideoPlayer';
import AssetDownloadButton from './AssetDownloadButton';
const MAX_THUMBNAILS = 4;
const THUMBNAIL_MARGIN = 12;
const THUMBNAIL_WIDTH = 174;
const THUMBNAIL_HEIGHT = 116;

const NoopOverhang = () => null;

const calculateThumbnailHeight = containerWidth => {
  const thumbnailWidth = containerWidth / MAX_THUMBNAILS;
  const widthToHeightRatio = THUMBNAIL_WIDTH / THUMBNAIL_HEIGHT;
  const thumbnailWidthHeight = thumbnailWidth / widthToHeightRatio;
  return thumbnailWidthHeight;
};

const calculateThumbnailWidth = containerWidth => {
  const computedMarginOffset = (MAX_THUMBNAILS - 1) * THUMBNAIL_MARGIN / MAX_THUMBNAILS;
  return containerWidth / MAX_THUMBNAILS - computedMarginOffset;
};

const ImageThumbnail = (_ref) => {
  let {
    altText,
    className,
    imageUrl,
    onClick,
    thumbnailWidth,
    thumbnailHeight
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["altText", "className", "imageUrl", "onClick", "thumbnailWidth", "thumbnailHeight"]);

  const auth = useSelector(state => state.auth);
  return (
    /*#__PURE__*/
    // @ts-expect-error props don't include className
    _jsx(UIClickable, Object.assign({}, rest, {
      className: className,
      onClick: onClick,
      children: /*#__PURE__*/_jsx(UIBox, {
        style: {
          height: thumbnailHeight,
          width: thumbnailWidth,
          overflowY: 'hidden',
          borderStyle: 'solid',
          borderWidth: 1,
          borderColor: GREAT_WHITE
        },
        children: /*#__PURE__*/_jsx(UIImage, {
          alt: altText,
          responsive: false,
          src: resize(imageUrl, {
            width: THUMBNAIL_WIDTH
          }, undefined, auth),
          width: "100%"
        })
      })
    }))
  );
};

const ShowAllThumbnail = styled(props => {
  const {
    offeringType
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["offeringType", "showAllLabel"]);

  return /*#__PURE__*/_jsx(ImageThumbnail, Object.assign({}, rest, {
    "data-hs-analytics-id": getAssetEventSelectorId('VIEW-ALL-IMAGES', offeringType),
    style: {
      textAlign: 'center'
    }
  }));
}).withConfig({
  displayName: "AssetScreenshots__ShowAllThumbnail",
  componentId: "sc-186mdbb-0"
})(["position:relative;&::after{content:'", "';position:absolute;top:0;right:0;bottom:0;left:0;display:flex;align-items:center;justify-content:center;background-color:", ";color:", ";font-weight:", ";}"], props => props.showAllLabel, rgba(FLAT_EARTH, 0.65), OLAF, WEB_FONT_MEDIUM_WEIGHT);
const StyledUIScrollContainer = styled(UIScrollContainer).withConfig({
  displayName: "AssetScreenshots__StyledUIScrollContainer",
  componentId: "sc-186mdbb-1"
})(["border-style:solid;border-width:1px;border-color:", ";"], GREAT_WHITE);
export default function AssetScreenshots({
  name,
  offeringType,
  onTrackInteraction,
  onTrackPageview,
  providerName,
  screenshots,
  assetDetails,
  assetScopes,
  videoUrl
}) {
  const firstScreenshot = screenshots[0];
  const {
    isMobileView,
    maxHeight
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.MD]: false
    },
    maxHeight: {
      xs: 300,
      sm: 480
    }
  });
  const lang = getLang();
  const {
    slug
  } = useParams();
  const shareUrl = useMemo(() => {
    return getPublicDetailPageFullUrl(lang, offeringType, slug);
  }, [lang, offeringType, slug]);
  const [modalOpen, setModalOpen] = useState(false);
  const [focusedImageIndex, setFocusedImageIndex] = useState(null);
  const [imageLoadStates, setImageLoadStates] = useState({});
  const [thumbnailWidth, setThumbnailWidth] = useState(THUMBNAIL_WIDTH);
  const [thumbnailHeight, setThumbnailHeight] = useState(THUMBNAIL_HEIGHT);
  const thumbnailContainerRef = useRef(null);
  const trackInteraction = useCallback((action, eventProperties = {}) => {
    onTrackInteraction(action, Object.assign({}, eventProperties, {
      section: 'screenshots'
    }));
  }, [onTrackInteraction]);
  useEffect(() => {
    const handleResize = () => {
      if (thumbnailContainerRef.current) {
        const containerWidth = thumbnailContainerRef.current.clientWidth;
        setThumbnailWidth(calculateThumbnailWidth(containerWidth));
        setThumbnailHeight(calculateThumbnailHeight(containerWidth));
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [setThumbnailWidth, setThumbnailHeight, thumbnailContainerRef]);
  const handleImageLoad = useCallback(imageUrl => {
    setImageLoadStates(Object.assign({}, imageLoadStates, {
      [imageUrl]: true
    }));
  }, [imageLoadStates]);
  const handlePreloadImage = useCallback(imageUrl => {
    if (imageLoadStates[imageUrl]) {
      return;
    }

    const image = new Image();
    image.src = imageUrl;

    image.onload = () => {
      handleImageLoad(imageUrl);
    };
  }, [handleImageLoad, imageLoadStates]);
  useLayoutEffect(() => {
    const cancelCallback = requestIdleCallback(() => {
      screenshots.forEach(({
        imageUrl
      }) => {
        handlePreloadImage(imageUrl);
      });
    });
    return () => {
      cancelCallback();
    };
  }, [handlePreloadImage, screenshots]);
  const renderedAssetDownloadButton = useMemo(() => /*#__PURE__*/_jsx(AssetDownloadButton, {
    offeringType: offeringType,
    assetDetails: assetDetails,
    assetScopes: assetScopes
  }), [assetDetails, assetScopes, offeringType]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsxs(UIFlex, {
      direction: "column",
      align: "stretch",
      children: [videoUrl ? /*#__PURE__*/_jsx(VideoPlayer, {
        onTrackInteraction: trackInteraction,
        playAnalyticsId: getAssetEventSelectorId('VIDEO-PLAY', offeringType),
        videoUrl: videoUrl
      }) : firstScreenshot ? /*#__PURE__*/_jsx(StyledUIScrollContainer, {
        maxHeight: maxHeight,
        Overhang: NoopOverhang,
        children: /*#__PURE__*/_jsx(UIClickable, {
          onClick: () => {
            setModalOpen(true);
            setFocusedImageIndex(0);
            trackInteraction('click main image');
          },
          children: /*#__PURE__*/_jsx(UIImage, {
            alt: firstScreenshot.altText,
            onLoad: () => {
              handleImageLoad(firstScreenshot.imageUrl);
            },
            src: firstScreenshot.imageUrl,
            width: 1160
          })
        })
      }) : null, /*#__PURE__*/_jsx(UIFlex, {
        align: "start",
        className: "m-top-3",
        justify: "start",
        ref: thumbnailContainerRef,
        children: screenshots.slice(0, MAX_THUMBNAILS).map(({
          altText,
          imageUrl
        }, index) => {
          let ImageThumbnailComponent = ImageThumbnail;
          let imageThumbnailProps = {
            altText,
            imageUrl,
            thumbnailWidth,
            thumbnailHeight,
            onClick: () => {
              setModalOpen(true);
              setFocusedImageIndex(index);
              trackInteraction('click image thumbnail', {
                itemIndex: index
              });
            },
            offeringType
          };

          if (screenshots.length > MAX_THUMBNAILS && index === MAX_THUMBNAILS - 1) {
            const count = isMobileView ? screenshots.length - (videoUrl ? 3 : 4) : screenshots.length;
            ImageThumbnailComponent = ShowAllThumbnail;
            imageThumbnailProps = Object.assign({}, imageThumbnailProps, {
              onClick: () => {
                setModalOpen(true);
                setFocusedImageIndex(null);
                trackInteraction('click show all images');
              },
              showAllLabel: unescapedText(`marketplace-assets-ui.themes.details.screenshots.${isMobileView ? 'showAllCount' : 'showAll'}`, {
                count
              })
            });
          }

          return /*#__PURE__*/_jsx(ImageThumbnailComponent, Object.assign({
            className: index !== MAX_THUMBNAILS - 1 ? 'm-right-3' : null
          }, imageThumbnailProps), `screenshot-thumbnail-${index}`);
        })
      })]
    }), modalOpen && /*#__PURE__*/_jsx(ScreenshotsModal, {
      shareUrl: shareUrl,
      shareAnalyticsId: getAssetEventSelectorId('SHARE', offeringType),
      onShareClick: () => trackInteraction('click share link'),
      ctaButton: renderedAssetDownloadButton,
      focusedImageIndex: focusedImageIndex,
      name: name,
      onClose: () => {
        setModalOpen(false);
      },
      onTrackInteraction: trackInteraction,
      onTrackPageview: onTrackPageview,
      providerName: providerName,
      screenshots: screenshots
    })]
  });
}